import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import ApiService from '../../../services/apiService';

interface ExportParams {
  fromDate: string;
  toDate: string;
  wuaId: number;
  wuaName: string;
}

const exportWuaReportToExcel = async ({ fromDate, toDate, wuaId, wuaName }: ExportParams) => {
  const workbook = new ExcelJS.Workbook();

  // 🧭 Utility function to create striped table
  const createTable = (
    sheet: ExcelJS.Worksheet,
    startRow: number,
    name: string,
    headers: string[],
    rows: any[][]
  ) => {
    const startCol = 2; // start from column B
    const tableRows = rows.map((r, i) => [i + 1, ...r]); // Add Sr No
    const startCell = `${String.fromCharCode(64 + startCol)}${startRow}`;
    sheet.addTable({
      name,
      ref: startCell,
      headerRow: true,
      style: {
        theme: 'TableStyleMedium2',
        showRowStripes: true,
      },
      columns: ['Sr No', ...headers].map((h) => ({ name: h })),
      rows: tableRows,
    });
  };

  // 🟢 Fetch API data
  const summaryResponse = await ApiService.axiosApi(`/chaklevelsummary/chaksummary`, {
    method: 'GET',
    params: { wuaId },
  });
  const summary = summaryResponse.success ? summaryResponse.data : {};

  const chakResponse = await ApiService.axiosApi(`/chaklevelsummary`, {
    method: 'GET',
    params: { pageNumber: 0, pageSize: 0, fromDate, toDate, wuaId },
  });
  const chakLevelSummary = chakResponse.success ? chakResponse.data.data : [];

  const subChakResponse = await ApiService.axiosApi(`/chaklevelsummary/subchak`, {
    method: 'GET',
    params: { wuaId, pageNumber: 0, pageSize: 0 },
  });
  const subChakData = subChakResponse.success ? subChakResponse.data.data : [];

  const beneficiaryResponse = await ApiService.axiosApi(`/chaklevelsummary/beneficiary`, {
    method: 'GET',
    params: { wuaId, pageNumber: 0, pageSize: 0 },
  });
  const beneficiaryData = beneficiaryResponse.success ? beneficiaryResponse.data.data : [];

  // 🟣 ---- FIRST SHEET: Chak Level Summary ----
  const chakSheet = workbook.addWorksheet('Chak Level Summary');

  // 🏷️ Main heading
  const mainHeading = chakSheet.addRow(['', 'WUA Water Distribution Report']);
  mainHeading.font = { bold: true, size: 16 };
  mainHeading.alignment = { horizontal: 'center' };
  chakSheet.mergeCells(`B${mainHeading.number}:E${mainHeading.number}`);

  chakSheet.addRow([]); // spacing

  // Add Summary Info (top section)
  const infoRows = [
    ['WUA Name', wuaName],
    ['Project / Scheme Name', 'Shivnala Irrigation Project'],
    ['Reporting Period', `From: ${fromDate}`, `To: ${toDate}`],
    ['Total No. of Chaks under WUA', summary.totalRoms ?? ''],
    ['Total No. of SubChaks under WUA', summary.totalSubChak ?? ''],
    ['Total Command Area (Ha)', summary.totalChakArea ?? ''],
    ['Total Water Distributed (Liters)', chakLevelSummary[0].grandTotalWaterDistributed],
    ['Total Beneficiaries under WUA', summary.totalBeneficiaries ?? ''],
  ];

  let currentRow = chakSheet.lastRow?.number ?? 2;
  infoRows.forEach((rowValues) => {
    const row = chakSheet.addRow(['', ...rowValues]);
    row.getCell(2).font = { bold: true };
    row.alignment = { vertical: 'middle' };
    currentRow++;
  });

  chakSheet.addRow([]); // spacing
  currentRow++;

  // 🟩 Section Heading: Chak Level Summary
  const chakTitle = chakSheet.addRow(['', 'Chak Level Summary']);
  chakTitle.font = { bold: true, size: 14 };
  chakTitle.alignment = { horizontal: 'left' };
  currentRow++;

  // Chak Level Summary Table
  createTable(
    chakSheet,
    chakSheet.lastRow!.number + 1,
    'ChakTable',
    ['Chak', 'No Of 8HAC SubChak', 'Total Area(HA)', 'Total Water Distributed'],
    chakLevelSummary.map((r: any) => [
      r.chakName,
      r.no_Of_8HAC_SubChak,
      r.chakArea,
      r.totalWaterDistrubuted,
    ])
  );

  // 🟣 ---- SECOND SHEET: SubChak ----
  const subChakSheet = workbook.addWorksheet('SubChak');

  const subChakHeading = subChakSheet.addRow(['', 'SubChak Details']);
  subChakHeading.font = { bold: true, size: 14 };
  subChakHeading.alignment = { horizontal: 'left' };
  subChakSheet.addRow([]);

  createTable(
    subChakSheet,
    subChakSheet.lastRow!.number + 1,
    'SubChakTable',
    ['SubChak', 'Area (HA)', 'No Of Beneficiaries'],
    subChakData.map((r: any) => [r.chakName, r.chakArea, r.noOfBeneficiary])
  );

  // 🟣 ---- THIRD SHEET: Beneficiaries ----
  const beneSheet = workbook.addWorksheet('Beneficiaries Info');

  const beneHeading = beneSheet.addRow(['', 'Beneficiaries Information']);
  beneHeading.font = { bold: true, size: 14 };
  beneHeading.alignment = { horizontal: 'left' };
  beneSheet.addRow([]);

  createTable(
    beneSheet,
    beneSheet.lastRow!.number + 1,
    'BeneficiaryTable',
    ['SubChak', 'Area (HA)', 'Name Of Beneficiaries','Plot'],
    beneficiaryData.map((r: any) => [r.chakName, r.cca_Acre, r.nameOfBeneficiary,r.plot_Name])
  );

  // ⚙️ Auto width for all sheets
  workbook.worksheets.forEach((sheet) => {
    (sheet.columns ?? []).forEach((column) => {
      if (!column) return;
      let maxLength = 0;
      column.eachCell?.({ includeEmpty: true }, (cell) => {
        const len = cell.value ? cell.value.toString().length : 10;
        if (len > maxLength) maxLength = len;
      });
      column.width = maxLength + 2;
    });
  });

  // 💾 Save Excel file
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `WUA_Water_Distribution_Report_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
};

export default exportWuaReportToExcel;
